<template>
  <div>
    <owal-loading-modal :show="submitting"/>
    <owal-form-field
      v-if="this.forms && this.forms.length > 1"
      :field="this.formSelectionField"
      @change="manualChooseForm"
      />
    <owal-panel-header
      v-if="this.activeForm && this.forms && this.forms.length > 1"
      :title="this.activeForm.name"
      position="middle"
      />
    <div
      v-if="this.activeForm && this.activeForm.groups && this.activeForm.groups.length > 0"
      class="owal panel form content"
      >
      <owal-form-field-group
        v-for="group in activeForm.groups"
        :key="group.name"
        :initialFields="group.fields"
        :name="group.name"
        :layout="group.layout"
        :initial-submission="initialSubmission"
        @change="inputChanged"
        :fieldTemplate="group.fieldTemplate"
        />
    </div>
    <div v-else-if="this.activeForm" class="owal panel form content">
      <owal-form-field-group
        :fields="this.activeForm.fields"
        :initial-submission="initialSubmission"
        @change="inputChanged"
        />
    </div>
    <transition>
      <div v-if="!embedded && readyToSubmit">
        <owal-button
          type="submit"
          class="owal submit button fade pink"
          @click.native="submit"
          @keyup.native.enter="submit"
          >
          <div class="content">
            Save
          </div>
        </owal-button>
      </div>
    </transition>
  </div>
</template>

<script>
import OwalFormField from './OwalFormField'
import OwalFormFieldGroup from './OwalFormFieldGroup'
import { FormSubmission } from '@/libraries/forms/base/formSubmission'
import { inputPanel } from '../mixins/inputPanel'
import * as forms from '@/libraries/forms/forms'
import OwalPanelHeader from '@/libraries/owal-ui/panels/OwalPanelHeader'
import OwalButton from '@/libraries/owal-ui/buttons/OwalButton'
import { SVC_C } from '@/libraries/api/base'
import OwalModalLoadingSpinner from '@/libraries/owal-ui/modals/OwalModalLoadingSpinner'

export default {
  name: 'OwalForm',
  components: {
    OwalFormFieldGroup,
    OwalPanelHeader,
    OwalFormField,
    OwalButton,
    'owal-loading-modal': OwalModalLoadingSpinner,
  },
  mixins: [inputPanel],
  props: {
    forms: {
      type: Array,
      default: undefined,
    },
    form: {
      type: Object,
      default: undefined,
    },
    noun: {
      type: String,
      default: 'Form',
    },
    endpoint: {
      type: String,
      default: undefined,
    },
    isInitialSubmission: {
      type: Boolean,
      default: undefined,
    },
    initialSubmission: {
      type: Object,
    },
    metadata: {
      type: Object,
      default: undefined,
    },
    submittingOverride: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      submission: undefined,
      activeForm: undefined,
      isSubmitting: false,
    }
  },
  computed: {
    formSelectionField () {
      if (!this.forms) {
        return undefined
      }

      if (this.forms.length === 0) {
        return undefined
      }

      const formField = new forms.FormField({
        type: forms.FormFieldType.SELECT,
        identifier: 'form',
        label: 'Please Select a ' + this.noun,
        options: this.forms.map(form => {
          return new forms.FormFieldSelectOption(form.name, form.identifier)
        }),
      })

      return formField
    },
    readyToSubmit () {
      if (!this.activeForm) {
        return false
      }

      const anyMissingFields = this.activeForm.fields.some(field => field.required && !this.submission.valueForField(field.identifier))
      return !anyMissingFields
    },
    editing () {
      if (this.isInitialSubmission !== undefined) {
        return !this.isInitialSubmission
      }
      return !!this.initialSubmission
    },
    submitting () {
      if (this.submittingOverride) {
        return this.submittingOverride
      } else return this.isSubmitting
    },
  },
  mounted () {
    let initialForm = this.form

    if (!this.form) {
      if (this.forms && this.forms.length === 1) {
        initialForm = this.forms[0]
      }
    } else this.setForm(initialForm)
  },
  methods: {
    inputChanged (payload) {
      const { value, identifier } = payload
      this.submission.setValueForField(value, identifier)
      this.$emit('change', this.submission)
    },
    manualChooseForm () {
      const index = event.target.selectedIndex - 1 // Account for "Select Option" entry.
      const form = this.forms[index]
      this.setForm(form)
    },
    setForm (form) {
      this.activeForm = form
      this.resetSubmission(form)
      this.$emit('form-change', form)
    },
    resetSubmission (form) {
      if (!form) {
        // console.log('OwalForm.vue: Cannot generate a submission for an undefined form.')
        return
      }

      if (!form.fields) {
        // console.log('OwalForm.vue: Cannot generate a submission for a form with no fields.')
        return
      }

      if (this.initialSubmission && this.initialSubmission.identifier === this.form.identifier) {
        this.submission = this.initialSubmission
      } else {
        this.submission = new FormSubmission(form.identifier, form.fields)
      }

      if (this.metadata) {
        this.submission.setMetadataWithObject(this.metadata)
      }

      this.$emit('change', this.submission)
    },
    submit () {
      if (!this.readyToSubmit) {
        console.log('OwalForm.vue: Not ready to submit, returning. Check required fields...')
        return
      }

      if (this.submitting) {
        console.log('OwalForm.vue: Submission already in progress, returning.')
        return
      }

      if (!this.activeForm) {
        console.log('OwalForm.vue: No form to submit, returning.')
        return
      }

      if (!this.submission) {
        console.log('OwalForm.vue: No submission to submit, returning.')
        return
      }

      if (this.endpoint) {
        this.defaultSubmit()
      } else {
        this.submitStart()
      }
    },
    submitStart () {
      const template = this.activeForm.submissionTemplate
      const form = template.processSubmission(this.submission)
      this.$emit('submit-start', form)
    },
    submitFinish ({ success, error, response }) {
      this.isSubmitting = false

      const details = {
        submission: this.submission,
        endpoint: this.endpoint,
        response,
        success,
        error,
      }

      // console.log(`OwalForm.vue: Submission finished. Details: ${JSON.stringify(details, null, 2)}`)
      this.$emit('submit-end', details)
    },
    defaultSubmit () {
      const template = this.activeForm.submissionTemplate
      const input = this.submission
      const payload = template.processSubmission(input)
      const endpoint = this.endpoint
      console.log(`Making request to ${endpoint}`)
      this.submitStart()

      const submitMethod = (this.editing ? SVC_C.patch : SVC_C.post).bind(SVC_C)

      submitMethod(endpoint, payload)
        .then(function (response) {
        // console.log(`Response: ${response}`)
          this.submitFinish({ success: true, response })
        }.bind(this))
        .catch(function (error) {
          console.log(`Error submitting form to ${this.endpoint}.`)
          this.submitFinish({ success: false, error })
        }.bind(this))
    },
  },
}
</script>

<style scoped>
/*
  v-transition
*/
.v-enter-active, .v-leave-active {
  transition: all 0.20s ease-in-out;
}

.v-enter, .v-leave-to {
  opacity: 0;
}
/*
Input color focusing.
*/
[required] input:focus:valid,
[required] input:valid {
  background-color: #dfd;
}

[required] input:invalid {
  background-color: #fdd;
}

.owal.submit.button {
  margin-top: 0.9375rem;
}

.owal.panel.middle.header {
  margin-left: -0.9375rem;
  margin-right: -0.9375rem;
  margin-bottom: 0.9375rem;
}

</style>
